import {
  Divider,
  FormControl,
  FormLabel,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { Fragment, useState } from 'react';

import {
  RoleAccordionBody,
  ScopeAccordion,
} from '@/components/permissions/scopeAccordion';
import { StepContainer } from '@/components/wizard/StepContainer';
import type { StepProps } from '@/components/wizard/StepProps';
import { useDAL } from '@/contexts/useDAL';

import type { UpsertGroupMappingState } from './upsertGroupMappingFlow';

import { EntityBadge } from '../users/userBadge';

export const UpsertGroupMappingStep = (
  props: StepProps<UpsertGroupMappingState>
) => {
  const dal = useDAL();
  const { body: samlProvidersBody } = dal.saml.list();
  const { body: projectsBody } = dal.projects.list();
  const [selectedIndex, setSelectedIndex] = useState(0);

  return (
    <>
      <StepContainer
        hideStepper
        onBackClick={props.back}
        canGoNext={() => {
          if (
            !props.wizardState.saml ||
            !props.wizardState.groupMapping.groupName ||
            !Object.keys(props.wizardState.groupMapping.roleToProjectsMappings)
              .length
          ) {
            return false;
          }

          const groupsByName = (
            props.wizardState.saml.groupToRoleMappingList || []
          ).filter(
            (x) => x.groupName === props.wizardState.groupMapping.groupName
          );

          if (props.wizardState.editMode) {
            if (groupsByName.length > 1) {
              return false;
            }
          } else {
            if (groupsByName.length) {
              return false;
            }
          }

          return true;
        }}
        nextButtonText={props.wizardState.editMode ? 'Save Changes' : 'Create'}
        backButtonText='Cancel'
        onNextClick={() => props.finish()}
      >
        <Stack gap='12px'>
          {props.wizardState.editMode && (
            <EntityBadge
              title={props.wizardState.groupMapping.groupName}
              subtitle={props.wizardState.saml?.providerName || ''}
              className='mx-[60px] pb-[12px]'
            />
          )}
          {!props.wizardState.editMode && (
            <>
              <Typography className='mx-[60px] font-semibold'>
                Group details
              </Typography>
              <Stack
                gap='16px'
                className='px-[60px] py-[12px] w-1/2'
                direction='row'
              >
                <FormControl size='small' className='w-1/2'>
                  <FormLabel>Group ID</FormLabel>
                  <TextField
                    value={props.wizardState.groupMapping.groupName}
                    onChange={(event) =>
                      props.setWizardState((state) => ({
                        ...state,
                        groupMapping: {
                          ...state.groupMapping,
                          groupName: event.target.value,
                        },
                      }))
                    }
                  />
                </FormControl>
                <FormControl size='small' className='w-1/2'>
                  <FormLabel>SAML identity provider</FormLabel>
                  <Select
                    size='small'
                    disabled={!samlProvidersBody?.samlIdentityProviders?.length}
                    value={
                      (samlProvidersBody?.samlIdentityProviders &&
                        props.wizardState.saml?.id) ||
                      ''
                    }
                    onChange={(event) => {
                      const saml =
                        samlProvidersBody?.samlIdentityProviders?.find(
                          (x) => x.id === event.target.value
                        );

                      if (saml) {
                        props.setWizardState((state) => ({
                          ...state,
                          saml,
                        }));
                      }
                    }}
                  >
                    {(samlProvidersBody?.samlIdentityProviders || []).map(
                      (saml) => (
                        <MenuItem key={saml.id} value={saml.id}>
                          {saml.providerName}
                        </MenuItem>
                      )
                    )}
                  </Select>
                </FormControl>
              </Stack>
            </>
          )}
          {!props.wizardState.editMode && (
            <>
              <Divider sx={{ mx: '-40px', my: '12px' }} />
              <Typography className='my-[12px] mx-[60px] font-semibold'>
                Permissions
              </Typography>
            </>
          )}
          <Tabs
            onChange={(e, i) => setSelectedIndex(i)}
            value={selectedIndex}
            className='px-[100px]'
            sx={{
              marginX: '-40px',

              '.MuiTabs-scroller': { display: 'flex' },
            }}
          >
            <Tab
              label='Global settings'
              className='text-[14px] font-normal'
              disableRipple
            />
            <Tab
              label='Projects'
              className='text-[14px] font-normal'
              disableRipple
            />
          </Tabs>
          {selectedIndex === 0 && (
            <Stack gap='12px' className='ml-[60px]'>
              <Typography className='my-[24px]'>
                Edit the permissions this group has on global settings
              </Typography>
              <RoleAccordionBody
                roleToProjectsMappings={
                  props.wizardState.groupMapping.roleToProjectsMappings
                }
                onChange={(roleToProjectsMappings) =>
                  props.setWizardState((state) => ({
                    ...state,
                    groupMapping: {
                      ...state.groupMapping,
                      roleToProjectsMappings: { ...roleToProjectsMappings },
                    },
                  }))
                }
              />
            </Stack>
          )}
          {selectedIndex === 1 && (
            <Stack gap='12px'>
              <Typography className='m-[24px_60px_12px_60px]'>
                Edit the permissions this group has on different projects
              </Typography>
              {projectsBody?.projects.map((project, index, projects) => (
                <Fragment key={project.id}>
                  <ScopeAccordion
                    project={project}
                    roleToProjectsMappings={
                      props.wizardState.groupMapping.roleToProjectsMappings
                    }
                    onChange={(roleToProjectsMappings) =>
                      props.setWizardState((state) => ({
                        ...state,
                        groupMapping: {
                          ...state.groupMapping,
                          roleToProjectsMappings: { ...roleToProjectsMappings },
                        },
                      }))
                    }
                  />
                  {index < projects.length - 1 && (
                    <Divider sx={{ mx: '-40px' }} />
                  )}
                </Fragment>
              ))}
            </Stack>
          )}
        </Stack>
      </StepContainer>
    </>
  );
};
